import { UIRouterGlobals } from "@uirouter/angular";
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ViewChild } from "@angular/core";
import { ValidatorFn, Validators } from "@angular/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import equal from "fast-deep-equal";
import { firstValueFrom, take } from "rxjs";
import { IGoalMethodologySettingsUI, OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { getDefaultAssigneeId } from "@gtmhub/goals/utils";
import { PlanningSessionService } from "@gtmhub/sessions/services/planning-session.service";
import { reduxStoreContainer } from "@gtmhub/state-management/state-management.module";
import { ITeamsStoreState, TeamsActions } from "@gtmhub/teams";
import { getCurrentUserId } from "@gtmhub/users";
import { AssigneesFacade } from "@webapp/assignees/services/assignees/assignees-facade.service";
import { ReduxStoreObserver } from "@webapp/core/state-management/redux-store-observer";
import { CfMap, ICustomField } from "@webapp/custom-fields/models/custom-fields.models";
import { filterCustomFieldsBySession } from "@webapp/custom-fields/utils/custom-fields-filter-util";
import { SessionsRepository } from "@webapp/sessions/services/sessions-repository.service";
import { PeopleSelectorRequest } from "@webapp/shared/components/people-selector/models/models";
import { FormGroupComponent } from "@webapp/shared/form/form-group.component";
import { createOwnersTypeValidator } from "./utils";

const OKR_NAME_MAX_CHARS_COUNT = 255;

@UntilDestroy()
@Component({
  selector: "create-okr-form",
  templateUrl: "./create-okr-form.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateOkrFormComponent {
  @ViewChild(FormGroupComponent)
  public form: FormGroupComponent<{
    sessionId: string;
    name: string;
    ownerIds: string[];
    private: boolean;
    customFields: CfMap;
  }>;

  public sessionId: string;
  public canOkrBePrivate = false;
  public customFieldsFilter: (fields: ICustomField[]) => ICustomField[];

  public nameConfig = {
    maxChars: OKR_NAME_MAX_CHARS_COUNT,
    validators: [Validators.maxLength(OKR_NAME_MAX_CHARS_COUNT)],
  };

  public ownerSelectorConfig: {
    request?: PeopleSelectorRequest;
    mode?: "default" | "multiple";
    validators: ValidatorFn[];
  } = {
    validators: [
      createOwnersTypeValidator(
        () => this.ownerSelectorConfig.request?.type || "both",
        () => this.assigneesFacade.getAssigneesIdMap()
      ),
    ],
  };

  public constructor(
    private routerGlobals: UIRouterGlobals,
    private okrMethodologyFacade: OkrMethodologyFacade,
    private changeDetector: ChangeDetectorRef,
    private assigneesFacade: AssigneesFacade,
    private planningSessionService: PlanningSessionService,
    private sessionsRepository: SessionsRepository,
    teamsActions: TeamsActions
  ) {
    // prefetch the teams - will be used when selecting a default owner
    reduxStoreContainer.reduxStore.dispatch(teamsActions.fetchTeamsIfMissing());

    // when the modal is opened from a session page, pre-fill that session in the form
    const hostSession = this.routerGlobals.params["planningSessionId"];
    this.handleSessionChange(hostSession);
  }

  public submitForm(): void {
    // eslint-disable-next-line no-console
    console.log(this.form.value);
  }

  public handleSessionChange(sessionId: string): void {
    this.sessionId = sessionId;
    this.updateOkrMethodology();
    this.updateCustomFieldsFilter();
  }

  private updateOkrMethodology(): void {
    this.sessionsRepository
      .getMap$()
      .pipe(take(1), untilDestroyed(this))
      .subscribe((sessions) => {
        const methodology = this.okrMethodologyFacade.getMethodologySettingsUIForGoalView({ private: false }, sessions.get(this.sessionId));

        this.canOkrBePrivate = methodology.assignGoalPermissions === "user";

        // configures the owner selector options
        this.setOwnerSelectorOptions(methodology);

        // validates and updates if needed the existing owner selection (order is important)
        this.removeOwnersWithTheMaxNumberOfOkrs(methodology)
          .then(() => this.removeSurplusOwners(methodology))
          .then(() => this.assignDefaultOwnerIfBlank(methodology))
          .then(() => {
            // force form control validation for the ui errors to kick-in if necessary
            this.form?.formGroup.get("ownerIds").updateValueAndValidity({ emitEvent: false });
            this.changeDetector.markForCheck();
          });
      });
  }

  private setOwnerSelectorOptions(methodology: IGoalMethodologySettingsUI): void {
    this.ownerSelectorConfig.mode = methodology.multipleOwnersPerGoalDisabled ? "default" : "multiple";
    this.ownerSelectorConfig.request = {
      ...new PeopleSelectorRequest(),
      ...(methodology.goalsPerOwner.sessionId ? { goalsLimitSetting: methodology.goalsPerOwner } : {}),
      type: methodology.assignGoalPermissions,
      permissionSetting: { enabled: true, permission: "ManageGoals" },
    };
  }

  /**
   * If the methodology restricts assignees from owning more than X number of OKRs,
   * filter out those already selected owners owning X or more OKRs.
   */
  private async removeOwnersWithTheMaxNumberOfOkrs(methodology: IGoalMethodologySettingsUI): Promise<void> {
    const selectedOwnerIds = this.form?.value.ownerIds || [];
    if (!this.sessionId || !selectedOwnerIds?.length) {
      return;
    }

    // convert to an id-map before iterating over each owner id
    const stats = (await this.planningSessionService.statsAssignees(this.sessionId, { assigneeIds: selectedOwnerIds.join(",") })).reduce(
      (map, owner) => ({ ...map, [owner.assigneeId]: owner.planningSessionStats }),
      {}
    );
    const validOwnerIds = selectedOwnerIds.filter((id) => stats[id]?.goalCount < methodology.goalsPerOwner.limit);

    if (!equal(selectedOwnerIds, validOwnerIds)) {
      this.form?.formGroup.get("ownerIds")?.setValue(validOwnerIds);
    }
  }

  /**
   * If the methodology restricts OKRs from being shared by multiple owners,
   * reduce the already selected selected number of owners to one.
   */
  private removeSurplusOwners(methodology: IGoalMethodologySettingsUI): void {
    const selectedOwnerIds = this.form?.value.ownerIds || [];
    if (methodology.multipleOwnersPerGoalDisabled && selectedOwnerIds.length > 1) {
      this.form?.formGroup.get("ownerIds")?.setValue([selectedOwnerIds[0]]);
    }
  }

  /**
   * If no owner is assigned and a session is selected, try  select a default owner.
   * If the default suggestion is invalid according to the session methodology - skip the assignment.
   */
  private async assignDefaultOwnerIfBlank(methodology: IGoalMethodologySettingsUI): Promise<void> {
    const selectedOwnerIds = this.form?.value.ownerIds || [];
    if (!this.sessionId || selectedOwnerIds.length > 0) {
      return;
    }

    const teams = (await firstValueFrom(new ReduxStoreObserver(reduxStoreContainer.reduxStore).whenFetched$<ITeamsStoreState>("teams"))).teams.items;
    const assignees = this.assigneesFacade.getAssigneesIdMap();

    const defaultOwnerId = getDefaultAssigneeId({
      teams: teams,
      assignees: assignees,
      currentUserId: getCurrentUserId(),
      assignObjectivePermissions: methodology.assignGoalPermissions,
    });

    const stats = await this.planningSessionService.stats(this.sessionId, { assigneeId: defaultOwnerId });
    if (stats.goalCount < methodology.goalsPerOwner.limit) {
      this.form?.formGroup.get("ownerIds")?.setValue([defaultOwnerId]);

      // mark dirty to allow error message to show up in case of session change, which invalidates the above set value
      this.form?.formGroup.get("ownerIds")?.markAsDirty();
    }
  }

  private updateCustomFieldsFilter(): void {
    // recreate on each session change occurence as to trigger the editable-custom-fields component fields to be filtered anew
    this.customFieldsFilter = (customFields): ICustomField[] => filterCustomFieldsBySession(this.sessionId, customFields);
  }
}
